import { useState, useEffect } from "react";
import {
  loadPayments,
  savePayments,
  getAccountPayments,
  addPayment,
  deletePayment,
  getPaymentSummary,
} from "./payments";

const formatCurrency = (val) =>
  new Intl.NumberFormat("en-US", { style: "currency", currency: "USD" }).format(val);

function getDaysUntilDue(paymentDate) {
  if (!paymentDate) return null;
  // paymentDate may be a full date string (e.g. "2026-04-25") or just a day number
  const dayNum = typeof paymentDate === "string" && paymentDate.includes("-")
    ? parseInt(paymentDate.split("-")[2])
    : parseInt(paymentDate);
  if (!dayNum || isNaN(dayNum)) return null;
  const today = new Date();
  const due = new Date(today.getFullYear(), today.getMonth(), dayNum);
  if (due < today) due.setMonth(due.getMonth() + 1);
  return Math.ceil((due - today) / (1000 * 60 * 60 * 24));
}

function getDaysBetween(fromDate, toDate) {
  if (!fromDate) return 30;
  const from = new Date(fromDate + "T00:00:00");
  const to = new Date(toDate + "T00:00:00");
  return Math.max(Math.ceil((to - from) / (1000 * 60 * 60 * 24)), 1);
}

function getDaysSinceLastPayment(lastPaymentDate) {
  if (!lastPaymentDate) return 30;
  const last = new Date(lastPaymentDate + "T00:00:00");
  const today = new Date();
  return Math.max(Math.ceil((today - last) / (1000 * 60 * 60 * 24)), 1);
}

function calcInterestAndPrincipal(amount, balance, rate, days) {
  const interest = parseFloat(
    Math.min(balance * (rate / 100 / 365) * days, amount).toFixed(2)
  );
  const principal = parseFloat(Math.max(amount - interest, 0).toFixed(2));
  return { interest, principal };
}

function buildAmortization(balance, annualRate, minPayment, lastPaymentDate, paymentDueDay) {
  const rows = [];
  // Parse all inputs as numbers to prevent NaN from string values
  let remaining = parseFloat(balance) || 0;
  const rate = parseFloat(annualRate) || 0;
  const payment = parseFloat(minPayment) || 0;
  const dueDay = paymentDueDay
    ? typeof paymentDueDay === "string" && paymentDueDay.includes("-")
      ? parseInt(paymentDueDay.split("-")[2])
      : parseInt(paymentDueDay)
    : null;
  const dailyRate = rate / 100 / 365;
  const maxMonths = 600;
  const today = new Date();

  for (let i = 1; i <= maxMonths && remaining > 0.005; i++) {
    let interest;
    let dueDate;

    if (i === 1 && lastPaymentDate && dueDay) {
      // Row 1: exact days from last payment date to next due date
      const last = new Date(lastPaymentDate + "T00:00:00");
      dueDate = new Date(last.getFullYear(), last.getMonth() + 1, dueDay);
      const days = Math.max(Math.floor((dueDate - last) / (1000 * 60 * 60 * 24)), 1);
      interest = remaining * dailyRate * days;
    } else if (dueDay) {
      // Rows 2+: exact days between consecutive due dates
      const baseMonth = today.getMonth() + i;
      const fromDue = new Date(today.getFullYear(), baseMonth - 1, dueDay);
      dueDate = new Date(today.getFullYear(), baseMonth, dueDay);
      const days = Math.floor((dueDate - fromDue) / (1000 * 60 * 60 * 24));
      interest = remaining * dailyRate * days;
    } else {
      // Fallback: standard monthly rate if no due day set
      interest = remaining * (rate / 100 / 12);
      dueDate = new Date(today.getFullYear(), today.getMonth() + i, 1);
    }

    const principal = Math.min(payment - interest, remaining);
    remaining = Math.max(remaining - principal, 0);

    // dueDateKey matches the format used by paidMonthKeys so paid months filter correctly
    const dueDateKey = `${dueDate.getFullYear()}-${dueDate.getMonth()}`;

    rows.push({
      month: i,
      payment,
      principal: principal < 0 ? 0 : principal,
      interest,
      remaining,
      dueDateKey,
    });
  }
  return rows;
}

function getPayoffMonths(balance, annualRate, monthlyPayment) {
  const monthlyRate = annualRate / 100 / 12;
  if (monthlyRate === 0) return Math.ceil(balance / monthlyPayment);
  if (monthlyPayment <= balance * monthlyRate) return null;
  return Math.ceil(
    Math.log(monthlyPayment / (monthlyPayment - balance * monthlyRate)) /
      Math.log(1 + monthlyRate)
  );
}

function getPayoffDate(months) {
  if (!months) return "Never (payment too low)";
  const d = new Date();
  d.setMonth(d.getMonth() + months);
  return d.toLocaleDateString("en-US", { month: "long", year: "numeric" });
}

function getTotalInterest(balance, annualRate, monthlyPayment) {
  const months = getPayoffMonths(balance, annualRate, monthlyPayment);
  if (!months) return null;
  return months * monthlyPayment - balance;
}

const PAYMENT_TYPES = [
  "Minimum Payment",
  "Extra Payment",
  "Full Payoff",
  "Partial Payment",
  "Other",
];

export default function AccountDetails({
  account,
  onBack,
  color = "#4F7FFA",
  onUpdateBalance,
  onUpdateLastPaymentDate,
  onEdit,
  onReminders,
  onDeletePayment,
  onAddPayment,
  onMarkMonthPaid,
  allPayments: allPaymentsProp,
  easyMode = false,
}) {
  const { name, type, balance, rate, minPayment, paymentDate, lastPaymentDate, originalLastPaymentDate } = account;
  const isMobile = window.innerWidth < 768;

  const daysSinceLastPayment = getDaysSinceLastPayment(lastPaymentDate);
  const today = new Date().toISOString().split("T")[0];

  // Calculate initial estimates based on today's date
  const initDays = getDaysBetween(lastPaymentDate, today);
  const initCalc = calcInterestAndPrincipal(minPayment, balance, rate, initDays);

  const [localPayments, setLocalPayments] = useState(loadPayments);
  const payments = allPaymentsProp || localPayments;
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [interestPopup, setInterestPopup] = useState(null); // { payment, prevDate }
  const [showAllAmort, setShowAllAmort] = useState(false);
  const [editingPayment, setEditingPayment] = useState(null);
  const [paymentForm, setPaymentForm] = useState({
    amount: String(minPayment),
    principal: String(initCalc.principal),
    interest: String(initCalc.interest),
    fees: "0",
    date: today,
    type: "Minimum Payment",
    notes: "",
  });
  const [paymentErrors, setPaymentErrors] = useState({});
  const [successBanner, setSuccessBanner] = useState(false);

  // Recalculate form when lastPaymentDate changes (e.g. after deleting a payment)
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (!showPaymentModal) {
      const newDays = getDaysBetween(lastPaymentDate, today);
      const newCalc = calcInterestAndPrincipal(minPayment, balance, rate, newDays);
      setPaymentForm((f) => ({
        ...f,
        principal: String(newCalc.principal),
        interest: String(newCalc.interest),
      }));
    }
  }, [lastPaymentDate]); // eslint-disable-line react-hooks/exhaustive-deps

  const accountPayments = getAccountPayments(payments, account.id);
  const summary = getPaymentSummary(accountPayments);

  const monthlyInterest = (balance * (rate / 100)) / 12;
  const dailyInterest = (balance * (rate / 100)) / 365;
  const monthlyPrincipal = minPayment - monthlyInterest;
  const payoffMonths = getPayoffMonths(balance, rate, minPayment);
  const payoffDate = getPayoffDate(payoffMonths);
  const totalInterest = getTotalInterest(balance, rate, minPayment);

  // Static values based on original balance (never changes as payments are made)
  const originalBalance = balance + (getAccountPayments(payments, account.id).reduce((s, p) => s + (p.principal || 0), 0));
  const staticPayoffMonths = getPayoffMonths(originalBalance, rate, minPayment);
  const staticPayoffDate = getPayoffDate(staticPayoffMonths);
  const staticTotalInterest = getTotalInterest(originalBalance, rate, minPayment);
  const amortization = buildAmortization(balance, rate, minPayment, lastPaymentDate, paymentDate);
  const interestPct = minPayment > 0 ? ((monthlyInterest / minPayment) * 100).toFixed(1) : 0;
  const principalPct = (100 - parseFloat(interestPct)).toFixed(1);
  const daysUntilDue = getDaysUntilDue(paymentDate);

  const dueBadge =
    daysUntilDue !== null
      ? daysUntilDue === 0
        ? { label: "Due today!", color: "#EF4444", bg: "#FEE2E2" }
        : daysUntilDue <= 3
        ? { label: `Due in ${daysUntilDue} day${daysUntilDue === 1 ? "" : "s"}`, color: "#F97316", bg: "#FFF7ED" }
        : daysUntilDue <= 7
        ? { label: `Due in ${daysUntilDue} days`, color: "#F59E0B", bg: "#FFFBEB" }
        : { label: `Due in ${daysUntilDue} days`, color: "#10B981", bg: "#F0FDF4" }
      : null;

  // Recalculate interest/principal when amount OR date changes
  const recalculate = (amount, date) => {
    const days = getDaysBetween(lastPaymentDate, date);
    const parsed = parseFloat(amount) || 0;
    const { interest, principal } = calcInterestAndPrincipal(parsed, balance, rate, days);
    return { interest: interest.toFixed(2), principal: principal.toFixed(2), days };
  };

  const handleAmountChange = (val) => {
    const { interest, principal } = recalculate(val, paymentForm.date);
    setPaymentForm((f) => ({ ...f, amount: val, interest, principal }));
  };

  const handleDateChange = (val) => {
    const { interest, principal } = recalculate(paymentForm.amount, val);
    setPaymentForm((f) => ({ ...f, date: val, interest, principal }));
  };

  // Days used for the current form date
  const formDays = getDaysBetween(lastPaymentDate, paymentForm.date);

  const validatePayment = () => {
    const e = {};
    if (!paymentForm.amount || isNaN(paymentForm.amount) || Number(paymentForm.amount) <= 0)
      e.amount = "Enter a valid amount";
    if (!paymentForm.date) e.date = "Enter a date";
    if (paymentForm.principal && isNaN(paymentForm.principal)) e.principal = "Enter a valid amount";
    if (paymentForm.interest && isNaN(paymentForm.interest)) e.interest = "Enter a valid amount";
    return e;
  };

  const handleLogPayment = () => {
    const e = validatePayment();
    if (Object.keys(e).length) { setPaymentErrors(e); return; }

    const payment = {
      amount: parseFloat(paymentForm.amount),
      principal: parseFloat(paymentForm.principal) || 0,
      interest: parseFloat(paymentForm.interest) || 0,
      fees: parseFloat(paymentForm.fees) || 0,
      date: paymentForm.date,
      type: paymentForm.type,
      notes: paymentForm.notes,
      prevDate: lastPaymentDate || null,
    };

    let updated;
    if (editingPayment) {
      const principalDiff = payment.principal - editingPayment.principal;
      const newBalance = Math.max(balance - principalDiff, 0);
      if (onUpdateBalance) onUpdateBalance(account.id, newBalance);
      const existing = payments[account.id] || [];
      const replacedList = existing.map((p) =>
        p.id === editingPayment.id ? { ...payment, id: editingPayment.id } : p
      );
      updated = { ...payments, [account.id]: replacedList };
      setLocalPayments(updated);
      savePayments(updated);
    } else {
      if (onUpdateBalance && payment.principal > 0) {
        onUpdateBalance(account.id, Math.max(balance - payment.principal, 0));
      }
      if (onAddPayment) {
        onAddPayment(account.id, payment);
      } else {
        updated = addPayment(payments, account.id, payment);
        setLocalPayments(updated);
        savePayments(updated);
      }
      // Store previous lastPaymentDate so delete can revert to it
      if (onUpdateLastPaymentDate) {
        // First store previous date on account
        account.previousLastPaymentDate = lastPaymentDate;
        onUpdateLastPaymentDate(account.id, payment.date);
      }
      // Auto mark as paid on dashboard payment plan
      if (onMarkMonthPaid) {
        onMarkMonthPaid(account.id);
      }
    }
    setShowPaymentModal(false);
    setEditingPayment(null);

    // Reset form with fresh calculations based on new last payment date
    const newDays = getDaysBetween(payment.date, today);
    const newCalc = calcInterestAndPrincipal(minPayment, balance, rate, newDays);
    setPaymentForm({
      amount: String(minPayment),
      principal: String(newCalc.principal),
      interest: String(newCalc.interest),
      fees: "0",
      date: today,
      type: "Minimum Payment",
      notes: "",
    });
    setPaymentErrors({});
    setSuccessBanner(true);
    setTimeout(() => setSuccessBanner(false), 3000);
  };

  const handleEditPayment = (payment) => {
    setEditingPayment(payment);
    setPaymentForm({
      amount: String(payment.amount),
      principal: String(payment.principal),
      interest: String(payment.interest),
      fees: String(payment.fees || 0),
      date: payment.date,
      type: payment.type,
      notes: payment.notes || "",
    });
    setShowPaymentModal(true);
  };

  const handleDeletePayment = (paymentId, principal) => {
    if (!window.confirm("Remove this payment? The balance will be restored.")) return;
    if (onUpdateBalance && principal > 0) {
      onUpdateBalance(account.id, balance + principal);
    }
    if (onDeletePayment) {
      onDeletePayment(account.id, paymentId);
    }
    const updated = deletePayment(payments, account.id, paymentId);
    setLocalPayments(updated);
    savePayments(updated);
    // Undo mark as paid if no payments remain this month
    const now = new Date();
    const remainingThisMonth = (updated[account.id] || []).filter(p => {
      const d = new Date(p.date + "T00:00:00");
      return d.getFullYear() === now.getFullYear() && d.getMonth() === now.getMonth();
    });
    if (remainingThisMonth.length === 0 && onMarkMonthPaid) {
      onMarkMonthPaid(account.id, true);
    }
    // Revert lastPaymentDate when deleting a payment
    const deletedPayment = accountPayments.find(p => p.id === paymentId);
    if (deletedPayment) {
      const remaining = (updated[account.id] || []);
      const revertTo = remaining.length > 0
        ? remaining.reduce((latest, p) => p.date > latest.date ? p : latest).date
        : account.previousLastPaymentDate || originalLastPaymentDate || null;
      if (onUpdateLastPaymentDate) {
        onUpdateLastPaymentDate(account.id, revertTo);
      }
    }
  };

  const closePaymentModal = () => {
    setShowPaymentModal(false);
    setEditingPayment(null);
    setPaymentErrors({});
    const resetCalc = calcInterestAndPrincipal(minPayment, balance, rate, initDays);
    setPaymentForm({
      amount: String(minPayment),
      principal: String(resetCalc.principal),
      interest: String(resetCalc.interest),
      fees: "0",
      date: today,
      type: "Minimum Payment",
      notes: "",
    });
  };

  return (
    <div style={styles.page}>
      {successBanner && (
        <div style={styles.successBanner}>✓ Payment logged successfully!</div>
      )}

      <main style={styles.main}>

        {/* Account Hero */}
        <div style={{ ...styles.heroCard, borderTop: `4px solid ${color}` }}>
          <div style={styles.heroTop}>
            <div>
              <div style={styles.heroName}>{name}</div>
              <div style={{ ...styles.heroType, color }}>{type}</div>
            </div>
            <div style={styles.heroRight}>
              <div style={styles.heroBalance}>
                <div style={styles.heroBalanceLabel}>Current Balance</div>
                <div style={{ ...styles.heroBalanceValue, color }}>{formatCurrency(balance)}</div>
              </div>
              <div style={{ display: "flex", gap: 8, flexWrap: "wrap", justifyContent: "flex-end" }}>
                {onEdit && (
                  <button style={{ ...styles.actionBtn, background: "#F3F4F6", color: "#555" }} onClick={onEdit}>
                    ✏️ Edit Account
                  </button>
                )}
                {onReminders && (
                  <button
                    style={{ ...styles.actionBtn, background: "#F3F4F6", color: "#555" }}
                    onClick={onReminders}
                    title="Payment reminders"
                  >
                    🔔 Reminders
                  </button>
                )}
                <button style={{ ...styles.actionBtn, background: color }} onClick={() => setShowPaymentModal(true)}>
                  + Log Payment
                </button>
              </div>
            </div>
          </div>

          <div style={styles.heroStats}>
            <div style={styles.heroStat}>
              <div style={styles.heroStatLabel}>Interest Rate</div>
              <div style={styles.heroStatValue}>{rate}% APR</div>
            </div>
            <div style={styles.heroStat}>
              <div style={styles.heroStatLabel}>Min. Payment</div>
              <div style={styles.heroStatValue}>{formatCurrency(minPayment)}/mo</div>
            </div>
            <div style={styles.heroStat}>
              <div style={styles.heroStatLabel}>Last Payment</div>
              <div style={styles.heroStatValue}>
                {lastPaymentDate
                  ? <span>
                      {new Date(lastPaymentDate + "T00:00:00").toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })}
                      <span style={{ fontSize: 11, color: "#888", marginLeft: 4 }}>({daysSinceLastPayment}d ago)</span>
                    </span>
                  : <span style={{ color: "#aaa" }}>Not set</span>}
              </div>
            </div>
            <div style={styles.heroStat}>
              <div style={styles.heroStatLabel}>Payment Due</div>
              <div style={styles.heroStatValue}>
                {paymentDate
                  ? <span>
                      {(() => {
                        const dayNum = typeof paymentDate === "string" && paymentDate.includes("-")
                          ? parseInt(paymentDate.split("-")[2])
                          : parseInt(paymentDate);
                        const today = new Date();
                        const due = new Date(today.getFullYear(), today.getMonth(), dayNum);
                        if (due < today) due.setMonth(due.getMonth() + 1);
                        return <>{due.toLocaleDateString("en-US", { month: "long", day: "numeric", year: "numeric" })}</>;
                      })()}
                      {dueBadge && (
                        <span style={{ ...styles.inlineBadge, color: dueBadge.color, background: dueBadge.bg }}>
                          {dueBadge.label}
                        </span>
                      )}
                    </span>
                  : <span style={{ color: "#aaa" }}>Not set</span>}
              </div>
            </div>
            <div style={styles.heroStat}>
              <div style={styles.heroStatLabel}>Projected Payoff Date</div>
              <div style={styles.heroStatValue}>{payoffDate}</div>
            </div>
            <div style={styles.heroStat}>
              <div style={styles.heroStatLabel}>Projected Interest</div>
              <div style={{ ...styles.heroStatValue, color: "#EF4444" }}>
                {totalInterest ? formatCurrency(totalInterest) : "N/A"}
              </div>
            </div>

          </div>
        </div>

        {!easyMode && <div style={styles.twoCol}>
          {/* Interest Breakdown */}
          <div style={styles.card}>
            <div style={styles.cardTitle}>Monthly Payment Breakdown</div>
            <div style={styles.cardSubtitle}>Where your {formatCurrency(minPayment)} goes each month</div>
            <div style={styles.breakdownBar}>
              <div style={{ ...styles.breakdownSegment, width: `${interestPct}%`, background: "#EF4444", borderRadius: "6px 0 0 6px" }} />
              <div style={{ ...styles.breakdownSegment, width: `${principalPct}%`, background: color, borderRadius: "0 6px 6px 0" }} />
            </div>
            <div style={styles.breakdownLegend}>
              <div style={styles.legendItem}>
                <div style={{ ...styles.legendDot, background: "#EF4444" }} />
                <div>
                  <div style={styles.legendLabel}>Interest ({interestPct}%)</div>
                  <div style={styles.legendValue}>{formatCurrency(monthlyInterest)}/mo</div>
                </div>
              </div>
              <div style={styles.legendItem}>
                <div style={{ ...styles.legendDot, background: color }} />
                <div>
                  <div style={styles.legendLabel}>Principal ({principalPct}%)</div>
                  <div style={styles.legendValue}>{monthlyPrincipal > 0 ? formatCurrency(monthlyPrincipal) : "$0.00"}/mo</div>
                </div>
              </div>
            </div>
            <div style={styles.interestNote}>
              💡 You're paying <strong style={{ color: "#EF4444" }}>{formatCurrency(monthlyInterest)}</strong> in interest every month — money that doesn't reduce your balance.
            </div>
            <div style={styles.calcBox}>
              <div style={styles.calcTitle}>How your interest is calculated</div>
              <div style={styles.calcRow}><span>Balance</span><span>{formatCurrency(balance)}</span></div>
              <div style={styles.calcRow}><span>× Annual Rate</span><span>{rate}%</span></div>
              <div style={styles.calcRow}><span>÷ 12 months</span><span>= {formatCurrency(monthlyInterest)}/mo</span></div>
              <div style={styles.calcRow}><span>÷ 365 days</span><span>= {formatCurrency(dailyInterest)}/day</span></div>
            </div>
          </div>

          {/* Payoff Info */}
          <div style={styles.card}>
            <div style={styles.cardTitle}>Payoff Summary</div>
            <div style={styles.cardSubtitle}>At current minimum payment</div>
            <div style={styles.payoffStats}>
              <div style={styles.payoffStat}>
                <div style={styles.payoffStatLabel}>Months to payoff</div>
                <div style={{ ...styles.payoffStatValue, color }}>{staticPayoffMonths ? `${staticPayoffMonths} months` : "∞"}</div>
              </div>
              <div style={styles.payoffStat}>
                <div style={styles.payoffStatLabel}>Payoff date</div>
                <div style={{ ...styles.payoffStatValue, color }}>{staticPayoffDate}</div>
              </div>
              <div style={styles.payoffStat}>
                <div style={styles.payoffStatLabel}>Total you'll pay</div>
                <div style={styles.payoffStatValue}>{staticTotalInterest ? formatCurrency(originalBalance + staticTotalInterest) : "N/A"}</div>
              </div>
              <div style={styles.payoffStat}>
                <div style={styles.payoffStatLabel}>Total interest cost</div>
                <div style={{ ...styles.payoffStatValue, color: "#EF4444" }}>{staticTotalInterest ? formatCurrency(staticTotalInterest) : "N/A"}</div>
              </div>
            </div>
            <div style={styles.interestNote}>
              📅 If you only pay the minimum, you'll pay <strong style={{ color: "#EF4444" }}>{staticTotalInterest ? formatCurrency(staticTotalInterest) : "unknown"}</strong> in interest alone before this debt is gone.
            </div>
            {summary.count > 0 && (() => {
              const originalBal = balance + summary.totalPrincipal;
              const progressPct = originalBal > 0 ? Math.min((summary.totalPrincipal / originalBal) * 100, 100) : 0;
              return (
                <div style={{ background: `linear-gradient(135deg, ${color}15, ${color}05)`, border: `1.5px solid ${color}33`, borderRadius: 12, padding: "16px 18px", marginTop: 8 }}>
                  <div style={{ fontSize: 12, fontWeight: 700, color, textTransform: "uppercase", letterSpacing: 0.5, marginBottom: 12 }}>🏆 Your Progress</div>
                  <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, marginBottom: 14 }}>
                    <div style={{ textAlign: "center" }}>
                      <div style={{ fontSize: 22, fontWeight: 800, color }}>{formatCurrency(summary.totalPrincipal)}</div>
                      <div style={{ fontSize: 11, color: "#888", marginTop: 2 }}>Principal Paid</div>
                    </div>
                    <div style={{ textAlign: "center" }}>
                      <div style={{ fontSize: 22, fontWeight: 800, color: "#EF4444" }}>{formatCurrency(summary.totalInterest)}</div>
                      <div style={{ fontSize: 11, color: "#888", marginTop: 2 }}>Interest Paid</div>
                    </div>
                  </div>
                  <div style={{ marginBottom: 6 }}>
                    <div style={{ display: "flex", justifyContent: "space-between", fontSize: 11, color: "#888", marginBottom: 4 }}>
                      <span>{progressPct.toFixed(1)}% paid off</span>
                      <span>{summary.count} payment{summary.count !== 1 ? "s" : ""} logged</span>
                    </div>
                    <div style={{ background: "#E5E7EB", borderRadius: 4, height: 8, overflow: "hidden" }}>
                      <div style={{ width: `${progressPct}%`, height: "100%", background: color, borderRadius: 4, transition: "width 0.5s" }} />
                    </div>
                  </div>
                  <div style={{ display: "flex", justifyContent: "space-between", fontSize: 12, color: "#888", marginTop: 8 }}>
                    <span>Total paid: <strong style={{ color: "#1a1a2e" }}>{formatCurrency(summary.totalPaid)}</strong></span>
                    <span>Remaining: <strong style={{ color }}>{formatCurrency(balance)}</strong></span>
                  </div>
                </div>
              );
            })()}
          </div>
        </div>}

        {/* Payment History */}
        <div style={styles.card}>
          <div style={styles.cardHeaderRow}>
            <div>
              <div style={styles.cardTitle}>Payment History</div>
              <div style={styles.cardSubtitle}>{summary.count} payment{summary.count !== 1 ? "s" : ""} logged</div>
            </div>
            <button style={{ ...styles.actionBtn, background: color }} onClick={() => setShowPaymentModal(true)}>
              + Log Payment
            </button>
          </div>
          {accountPayments.length === 0 ? (
            <div style={styles.emptyPayments}>
              <div style={styles.emptyPaymentsIcon}>💳</div>
              <div style={styles.emptyPaymentsTitle}>No payments logged yet</div>
              <div style={styles.emptyPaymentsText}>Log your first payment to start tracking your progress.</div>
            </div>
          ) : isMobile ? (
            <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
              {accountPayments.map((p, i) => (
                <div key={p.id} style={{ background: i % 2 === 0 ? "#FAFAFA" : "#fff", borderRadius: 10, padding: "12px 14px", border: "1px solid #F0F0F5" }}>
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 6 }}>
                    <div>
                      <span style={{ ...styles.typeBadge, background: color + "22", color }}>{p.type}</span>
                      <div style={{ fontSize: 11, color: "#888", marginTop: 4 }}>{new Date(p.date + "T00:00:00").toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })}</div>
                    </div>
                    <div style={{ textAlign: "right" }}>
                      <div style={{ fontWeight: 700, fontSize: 15, cursor: "pointer", textDecoration: "underline dotted", textUnderlineOffset: 3 }}
                        onClick={() => {
                          const idx = accountPayments.indexOf(p);
                          const prevPayment = accountPayments[idx + 1];
                          setInterestPopup({ payment: p, prevDate: p.prevDate || prevPayment?.date || originalLastPaymentDate || lastPaymentDate });
                        }}>
                        {formatCurrency(p.amount)}
                      </div>
                      <div style={{ display: "flex", gap: 6, justifyContent: "flex-end", marginTop: 4 }}>
                        <button style={styles.editPaymentBtn} onClick={() => handleEditPayment(p)} title="Edit">✏️</button>
                        <button style={styles.deletePaymentBtn} onClick={() => handleDeletePayment(p.id, p.principal)} title="Delete">✕</button>
                      </div>
                    </div>
                  </div>
                  <div style={{ display: "flex", gap: 12, fontSize: 12 }}>
                    <span>Principal: <strong style={{ color }}>{formatCurrency(p.principal)}</strong></span>
                    <span>Interest: <strong style={{ color: "#EF4444" }}>{formatCurrency(p.interest)}</strong></span>
                    {p.fees > 0 && <span>Fees: <strong style={{ color: "#F59E0B" }}>{formatCurrency(p.fees)}</strong></span>}
                  </div>
                  {p.notes && <div style={{ fontSize: 11, color: "#888", marginTop: 4 }}>{p.notes}</div>}
                </div>
              ))}
            </div>
          ) : (
            <div style={styles.tableWrap}>
              <table style={styles.table}>
                <thead>
                  <tr>
                    {["Date", "Type", "Amount", "Principal", "Interest", "Fees", "Notes", ""].map((h) => (
                      <th key={h} style={styles.th}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {accountPayments.map((p, i) => (
                    <tr key={p.id} style={i % 2 === 0 ? styles.trEven : {}}>
                      <td style={styles.td}>{new Date(p.date + "T00:00:00").toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })}</td>
                      <td style={styles.td}><span style={{ ...styles.typeBadge, background: color + "22", color }}>{p.type}</span></td>
                      <td style={{ ...styles.td, fontWeight: 700 }}>
                        <span style={{ cursor: "pointer", textDecoration: "underline dotted", textUnderlineOffset: 3 }} title="Click to see interest calculation"
                          onClick={() => {
                            const idx = accountPayments.indexOf(p);
                            const prevPayment = accountPayments[idx + 1];
                            setInterestPopup({ payment: p, prevDate: p.prevDate || prevPayment?.date || originalLastPaymentDate || lastPaymentDate });
                          }}>
                          {formatCurrency(p.amount)}
                        </span>
                      </td>
                      <td style={{ ...styles.td, color }}>{formatCurrency(p.principal)}</td>
                      <td style={{ ...styles.td, color: "#EF4444" }}>{formatCurrency(p.interest)}</td>
                      <td style={{ ...styles.td, color: "#F59E0B" }}>{p.fees > 0 ? formatCurrency(p.fees) : "—"}</td>
                      <td style={{ ...styles.td, color: "#888", fontSize: 12 }}>{p.notes || "—"}</td>
                      <td style={styles.td}>
                        <div style={{ display: "flex", gap: 6 }}>
                          <button style={styles.editPaymentBtn} onClick={() => handleEditPayment(p)} title="Edit payment">✏️</button>
                          <button style={styles.deletePaymentBtn} onClick={() => handleDeletePayment(p.id, p.principal)} title="Delete payment">✕</button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Remaining Payment Schedule */}
        {(() => {
          // Build set of month-year keys that have logged payments
          const paidMonthKeys = new Set(
            accountPayments.map((p) => {
              const d = new Date(p.date + "T00:00:00");
              return `${d.getFullYear()}-${d.getMonth()}`;
            })
          );

          // Filter out rows whose due date month has already been paid
          const unpaidRows = amortization.filter((row) => !paidMonthKeys.has(row.dueDateKey));

          const displayRows = showAllAmort ? unpaidRows : unpaidRows.slice(0, 24);

          return (
            <div style={styles.card}>
              <div style={styles.cardTitle}>Remaining Payment Schedule</div>
              <div style={styles.cardSubtitle}>
                {unpaidRows.length} payment{unpaidRows.length !== 1 ? "s" : ""} remaining · paid months are automatically removed
              </div>
              {isMobile ? (
                <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                  {displayRows.map((row, i) => {
                    let dateStr;
                    if (lastPaymentDate && paymentDate) {
                      const last = new Date(lastPaymentDate + "T00:00:00");
                      const due = new Date(last.getFullYear(), last.getMonth() + row.month, parseInt(paymentDate));
                      dateStr = due.toLocaleDateString("en-US", { month: "short", year: "numeric" });
                    } else {
                      const d = new Date();
                      d.setMonth(d.getMonth() + row.month - 1);
                      dateStr = d.toLocaleDateString("en-US", { month: "short", year: "numeric" });
                    }
                    return (
                      <div key={i} style={{ background: i % 2 === 0 ? "#FAFAFA" : "#fff", borderRadius: 10, padding: "10px 14px", border: "1px solid #F0F0F5" }}>
                        <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 4 }}>
                          <span style={{ fontSize: 12, color: "#888" }}>#{row.month} · {dateStr}</span>
                          <span style={{ fontWeight: 700 }}>{formatCurrency(row.payment)}</span>
                        </div>
                        <div style={{ display: "flex", gap: 12, fontSize: 12 }}>
                          <span>Principal: <strong style={{ color }}>{formatCurrency(row.principal)}</strong></span>
                          <span>Interest: <strong style={{ color: "#EF4444" }}>{formatCurrency(row.interest)}</strong></span>
                          <span style={{ color: "#888" }}>Bal: {formatCurrency(row.remaining)}</span>
                        </div>
                      </div>
                    );
                  })}
                </div>
              ) : (
              <div style={styles.tableWrap}>
                <table style={styles.table}>
                  <thead>
                    <tr>
                      {["#", "Date", "Payment", "Principal", "Interest", "Remaining Balance"].map((h) => (
                        <th key={h} style={styles.th}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {displayRows.map((row, i) => {
                      let dateStr;
                      if (lastPaymentDate && paymentDate) {
                        const last = new Date(lastPaymentDate + "T00:00:00");
                        const due = new Date(last.getFullYear(), last.getMonth() + row.month, parseInt(paymentDate));
                        dateStr = due.toLocaleDateString("en-US", { month: "short", year: "numeric" });
                      } else {
                        const d = new Date();
                        d.setMonth(d.getMonth() + row.month - 1);
                        dateStr = d.toLocaleDateString("en-US", { month: "short", year: "numeric" });
                      }
                      return (
                        <tr key={i} style={i % 2 === 0 ? styles.trEven : {}}>
                          <td style={styles.td}>{row.month}</td>
                          <td style={styles.td}>{dateStr}</td>
                          <td style={styles.td}>{formatCurrency(row.payment)}</td>
                          <td style={{ ...styles.td, color }}>{formatCurrency(row.principal)}</td>
                          <td style={{ ...styles.td, color: "#EF4444" }}>{formatCurrency(row.interest)}</td>
                          <td style={{ ...styles.td, fontWeight: 600 }}>{formatCurrency(row.remaining)}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
              )}
              {unpaidRows.length > 24 && (
                <div style={{ padding: "16px", textAlign: "center", borderTop: "1px solid #F0F0F5" }}>
                  <button
                    style={{ background: "none", border: "1.5px solid #ddd", borderRadius: 8, padding: "8px 20px", fontSize: 13, fontWeight: 600, cursor: "pointer", color: "#555" }}
                    onClick={() => setShowAllAmort(s => !s)}
                  >
                    {showAllAmort ? "▲ Show less" : `▼ Show all ${unpaidRows.length} payments`}
                  </button>
                </div>
              )}
            </div>
          );
        })()}
      </main>

      {/* Interest Calculation Popup */}
      {interestPopup && (() => {
        const { payment, prevDate } = interestPopup;
        const payDate = new Date(payment.date + "T00:00:00");
        const prevDateObj = prevDate ? new Date(prevDate + "T00:00:00") : null;
        const days = prevDateObj ? Math.max(Math.ceil((payDate - prevDateObj) / (1000 * 60 * 60 * 24)), 1) : null;
        const balanceAtTime = balance + (accountPayments
          .filter(p => new Date(p.date + "T00:00:00") >= payDate)
          .reduce((s, p) => s + (p.principal || 0), 0));
        // const calcInterest = days ? (balanceAtTime * (rate / 100 / 365) * days) : null;
        return (
          <div style={styles.modalOverlay} onClick={() => setInterestPopup(null)}>
            <div style={{ ...styles.modal, maxWidth: 400 }} onClick={e => e.stopPropagation()}>
              <div style={styles.modalHeader}>
                <h3 style={styles.modalTitle}>Interest Calculation</h3>
                <button style={styles.modalClose} onClick={() => setInterestPopup(null)}>✕</button>
              </div>
              <div style={{ padding: "20px 24px", display: "flex", flexDirection: "column", gap: 0 }}>
                <div style={{ fontSize: 13, color: "#888", marginBottom: 16 }}>
                  Payment on {payDate.toLocaleDateString("en-US", { month: "long", day: "numeric", year: "numeric" })}
                </div>
                <div style={{ background: "#F8F9FC", borderRadius: 10, padding: "14px 16px", marginBottom: 16 }}>
                  <div style={{ fontSize: 11, fontWeight: 700, color: "#888", textTransform: "uppercase", letterSpacing: 0.5, marginBottom: 10 }}>How interest was calculated</div>
                  <div style={{ display: "flex", justifyContent: "space-between", fontSize: 13, padding: "5px 0", borderBottom: "1px solid #eee" }}>
                    <span>Balance at payment time</span><span style={{ fontWeight: 600 }}>{formatCurrency(balanceAtTime)}</span>
                  </div>
                  <div style={{ display: "flex", justifyContent: "space-between", fontSize: 13, padding: "5px 0", borderBottom: "1px solid #eee" }}>
                    <span>× Annual rate</span><span style={{ fontWeight: 600 }}>{rate}%</span>
                  </div>
                  <div style={{ display: "flex", justifyContent: "space-between", fontSize: 13, padding: "5px 0", borderBottom: "1px solid #eee" }}>
                    <span>÷ 365 days</span><span style={{ fontWeight: 600 }}>= {formatCurrency(balanceAtTime * rate / 100 / 365)}/day</span>
                  </div>
                  {days && (
                    <div style={{ display: "flex", justifyContent: "space-between", fontSize: 13, padding: "5px 0", borderBottom: "1px solid #eee" }}>
                      <span>× <strong>{days}</strong> days since last payment</span>
                      <span style={{ fontWeight: 600 }}>
                        {prevDate ? `(${new Date(prevDate + "T00:00:00").toLocaleDateString("en-US", { month: "short", day: "numeric" })} → ${payDate.toLocaleDateString("en-US", { month: "short", day: "numeric" })})` : ""}
                      </span>
                    </div>
                  )}
                  <div style={{ display: "flex", justifyContent: "space-between", fontSize: 14, padding: "8px 0 2px", fontWeight: 700 }}>
                    <span>= Interest charged</span>
                    <span style={{ color: "#EF4444" }}>{formatCurrency(payment.interest)}</span>
                  </div>
                </div>
                <div style={{ background: "#F0FDF4", borderRadius: 10, padding: "14px 16px", border: "1px solid #BBF7D0" }}>
                  <div style={{ fontSize: 11, fontWeight: 700, color: "#888", textTransform: "uppercase", letterSpacing: 0.5, marginBottom: 10 }}>Payment breakdown</div>
                  <div style={{ display: "flex", justifyContent: "space-between", fontSize: 13, padding: "5px 0", borderBottom: "1px solid #d1fae5" }}>
                    <span>Total payment</span><span style={{ fontWeight: 600 }}>{formatCurrency(payment.amount)}</span>
                  </div>
                  <div style={{ display: "flex", justifyContent: "space-between", fontSize: 13, padding: "5px 0", borderBottom: "1px solid #d1fae5" }}>
                    <span>− Interest</span><span style={{ fontWeight: 600, color: "#EF4444" }}>− {formatCurrency(payment.interest)}</span>
                  </div>
                  <div style={{ display: "flex", justifyContent: "space-between", fontSize: 13, padding: "5px 0", borderBottom: "1px solid #d1fae5" }}>
                    <span>− Fees</span><span style={{ fontWeight: 600, color: "#F59E0B" }}>− {formatCurrency(payment.fees || 0)}</span>
                  </div>
                  <div style={{ display: "flex", justifyContent: "space-between", fontSize: 14, padding: "8px 0 2px", fontWeight: 700 }}>
                    <span>= Principal paid</span><span style={{ color }}>{formatCurrency(payment.principal)}</span>
                  </div>
                </div>
              </div>
              <div style={{ padding: "0 24px 20px", display: "flex", justifyContent: "flex-end" }}>
                <button style={{ background: "#F3F4F6", color: "#555", border: "none", borderRadius: 8, padding: "9px 18px", fontWeight: 600, fontSize: 14, cursor: "pointer" }} onClick={() => setInterestPopup(null)}>Close</button>
              </div>
            </div>
          </div>
        );
      })()}

      {/* Log / Edit Payment Modal */}
      {showPaymentModal && (
        <div style={styles.modalOverlay} onClick={closePaymentModal}>
          <div style={styles.modal} onClick={(e) => e.stopPropagation()}>
            <div style={styles.modalHeader}>
              <h3 style={styles.modalTitle}>{editingPayment ? `Edit Payment — ${name}` : `Log a Payment — ${name}`}</h3>
              <button style={styles.modalClose} onClick={closePaymentModal}>✕</button>
            </div>
            <div style={styles.modalBody}>
              <div style={styles.formGroup}>
                <label style={styles.label}>Payment Amount ($)</label>
                <input
                  style={{ ...styles.input, ...(paymentErrors.amount ? styles.inputError : {}) }}
                  type="number" min="0" placeholder="e.g. 85.00"
                  value={paymentForm.amount}
                  onChange={(e) => handleAmountChange(e.target.value)}
                />
                {paymentErrors.amount && <div style={styles.errorText}>{paymentErrors.amount}</div>}
              </div>
              <div style={styles.formGroup}>
                <label style={styles.label}>Payment Date</label>
                <input
                  style={{ ...styles.input, ...(paymentErrors.date ? styles.inputError : {}) }}
                  type="date" value={paymentForm.date}
                  onChange={(e) => handleDateChange(e.target.value)}
                />
                {paymentErrors.date && <div style={styles.errorText}>{paymentErrors.date}</div>}
              </div>
              <div style={styles.splitRow}>
                <div style={styles.formGroup}>
                  <label style={styles.label}>Principal Paid ($)</label>
                  <input
                    style={{ ...styles.input, ...(paymentErrors.principal ? styles.inputError : {}) }}
                    type="number" min="0" placeholder="Auto-calculated"
                    value={paymentForm.principal}
                    onChange={(e) => setPaymentForm((f) => ({ ...f, principal: e.target.value }))}
                    onBlur={(e) => {
                      const amt = parseFloat(paymentForm.amount) || 0;
                      const fees = parseFloat(paymentForm.fees) || 0;
                      const available = Math.max(amt - fees, 0);
                      const val = Math.min(parseFloat(e.target.value) || 0, available);
                      setPaymentForm((f) => ({ ...f, principal: val.toFixed(2), interest: Math.max(available - val, 0).toFixed(2) }));
                    }}
                  />
                  {paymentErrors.principal && <div style={styles.errorText}>{paymentErrors.principal}</div>}
                </div>
                <div style={styles.formGroup}>
                  <label style={styles.label}>Interest Paid ($)</label>
                  <input
                    style={{ ...styles.input, ...(paymentErrors.interest ? styles.inputError : {}) }}
                    type="number" min="0" placeholder="Auto-calculated"
                    value={paymentForm.interest}
                    onChange={(e) => setPaymentForm((f) => ({ ...f, interest: e.target.value }))}
                    onBlur={(e) => {
                      const amt = parseFloat(paymentForm.amount) || 0;
                      const fees = parseFloat(paymentForm.fees) || 0;
                      const available = Math.max(amt - fees, 0);
                      const val = Math.min(parseFloat(e.target.value) || 0, available);
                      setPaymentForm((f) => ({ ...f, interest: val.toFixed(2), principal: Math.max(available - val, 0).toFixed(2) }));
                    }}
                  />
                  {paymentErrors.interest && <div style={styles.errorText}>{paymentErrors.interest}</div>}
                </div>
              </div>
              <div style={styles.formGroup}>
                <label style={styles.label}>Fees ($) <span style={{ color: "#999", fontWeight: 400 }}>(optional)</span></label>
                <input
                  style={styles.input}
                  type="number" min="0" placeholder="0.00"
                  value={paymentForm.fees}
                  onChange={(e) => {
                    const fees = parseFloat(e.target.value) || 0;
                    const amt = parseFloat(paymentForm.amount) || 0;
                    const available = Math.max(amt - fees, 0);
                    const interest = Math.min(parseFloat(paymentForm.interest) || 0, available);
                    const principal = Math.max(available - interest, 0);
                    setPaymentForm((f) => ({ ...f, fees: e.target.value, principal: principal.toFixed(2), interest: interest.toFixed(2) }));
                  }}
                />
              </div>
              <div style={styles.autoCalcNote}>
                💡 Estimated based on <strong>{formDays} days</strong> between{" "}
                {lastPaymentDate
                  ? new Date(lastPaymentDate + "T00:00:00").toLocaleDateString("en-US", { month: "short", day: "numeric" })
                  : "last payment (not set)"}
                {" "}and the selected payment date. Adjust to match your statement if needed.
              </div>
              <div style={styles.formGroup}>
                <label style={styles.label}>Payment Type</label>
                <select style={styles.input} value={paymentForm.type} onChange={(e) => setPaymentForm((f) => ({ ...f, type: e.target.value }))}>
                  {PAYMENT_TYPES.map((t) => <option key={t}>{t}</option>)}
                </select>
              </div>
              <div style={styles.formGroup}>
                <label style={styles.label}>Notes (optional)</label>
                <input
                  style={styles.input} type="text" placeholder="e.g. March payment"
                  value={paymentForm.notes}
                  onChange={(e) => setPaymentForm((f) => ({ ...f, notes: e.target.value }))}
                />
              </div>
            </div>
            <div style={styles.modalFooter}>
              <button style={styles.cancelBtn} onClick={closePaymentModal}>Cancel</button>
              <button style={{ ...styles.actionBtn, background: color, padding: "10px 20px" }} onClick={handleLogPayment}>
                {editingPayment ? "Save Changes" : "Log Payment"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

const styles = {
  page: { minHeight: "100vh", background: "#F8F9FC", fontFamily: "'Segoe UI', system-ui, sans-serif", color: "#1a1a2e" },
  successBanner: { position: "fixed", bottom: 24, right: 24, background: "#10B981", color: "#fff", padding: "10px 18px", borderRadius: 10, fontSize: 13, fontWeight: 600, zIndex: 9999, boxShadow: "0 4px 12px rgba(0,0,0,0.15)" },
  header: { background: "#1a1a2e", padding: "0 24px", boxShadow: "0 2px 12px rgba(0,0,0,0.15)" },
  headerInner: { maxWidth: 1100, margin: "0 auto", display: "flex", alignItems: "center", height: 64 },
  logo: { fontSize: 22, fontWeight: 800, color: "#fff", letterSpacing: 3 },
  logoSub: { fontSize: 11, color: "#8899BB", letterSpacing: 1, marginTop: 2, marginLeft: 10 },
  main: { maxWidth: 1100, margin: "0 auto", padding: "32px 24px" },
  backBtn: { background: "none", border: "1.5px solid #ddd", borderRadius: 8, padding: "8px 16px", fontSize: 14, cursor: "pointer", color: "#555", marginBottom: 24, fontWeight: 500 },
  heroCard: { background: "#fff", borderRadius: 14, padding: "28px 28px 24px", boxShadow: "0 1px 6px rgba(0,0,0,0.08)", marginBottom: 24 },
  heroTop: { display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 24 },
  heroName: { fontSize: 24, fontWeight: 800, marginBottom: 4 },
  heroType: { fontSize: 13, fontWeight: 600, textTransform: "uppercase", letterSpacing: 0.5 },
  heroRight: { display: "flex", flexDirection: "column", alignItems: "flex-end", gap: 12 },
  heroBalance: { textAlign: "right" },
  heroBalanceLabel: { fontSize: 12, color: "#888", marginBottom: 4 },
  heroBalanceValue: { fontSize: 32, fontWeight: 800 },
  actionBtn: { color: "#fff", border: "none", borderRadius: 8, padding: "9px 16px", fontWeight: 600, fontSize: 13, cursor: "pointer" },
  heroStats: { display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8 },
  heroStat: { background: "#F8F9FC", borderRadius: 10, padding: "10px 12px" },
  heroStatLabel: { fontSize: 11, color: "#999", fontWeight: 600, textTransform: "uppercase", marginBottom: 5 },
  heroStatValue: { fontSize: 15, fontWeight: 700 },
  inlineBadge: { display: "inline-block", fontSize: 11, fontWeight: 700, padding: "2px 8px", borderRadius: 20, marginLeft: 8 },
  twoCol: { display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(320px, 1fr))", gap: 20, marginBottom: 24 },
  card: { background: "#fff", borderRadius: 14, padding: "24px", boxShadow: "0 1px 6px rgba(0,0,0,0.08)", marginBottom: 24, overflow: "visible" },
  cardHeaderRow: { display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 20 },
  cardTitle: { fontSize: 16, fontWeight: 700, marginBottom: 4 },
  cardSubtitle: { fontSize: 13, color: "#888", marginBottom: 20 },
  breakdownBar: { display: "flex", height: 18, borderRadius: 6, overflow: "hidden", marginBottom: 16 },
  breakdownSegment: { height: "100%", transition: "width 0.5s" },
  breakdownLegend: { display: "flex", gap: 20, marginBottom: 16 },
  legendItem: { display: "flex", alignItems: "center", gap: 10 },
  legendDot: { width: 12, height: 12, borderRadius: "50%", flexShrink: 0 },
  legendLabel: { fontSize: 12, color: "#888", marginBottom: 2 },
  legendValue: { fontSize: 15, fontWeight: 700 },
  interestNote: { background: "#FFF8F0", border: "1px solid #FFE0B2", borderRadius: 8, padding: "12px 14px", fontSize: 13, color: "#555", marginBottom: 16, lineHeight: 1.5 },
  calcBox: { background: "#F8F9FC", borderRadius: 10, padding: "14px 16px", overflow: "visible" },
  paymentSummaryBox: { background: "#F0FDF4", borderRadius: 10, padding: "14px 16px", marginTop: 16, border: "1px solid #BBF7D0" },
  calcTitle: { fontSize: 12, fontWeight: 700, color: "#888", textTransform: "uppercase", letterSpacing: 0.5, marginBottom: 10 },
  calcRow: { display: "flex", justifyContent: "space-between", alignItems: "center", fontSize: 13, padding: "4px 0", borderBottom: "1px solid #eee", gap: 12, flexWrap: "nowrap" },
  payoffStats: { display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14, marginBottom: 16 },
  payoffStat: { background: "#F8F9FC", borderRadius: 10, padding: "14px 16px" },
  payoffStatLabel: { fontSize: 11, color: "#999", fontWeight: 600, textTransform: "uppercase", marginBottom: 5 },
  payoffStatValue: { fontSize: 15, fontWeight: 700, color: "#1a1a2e" },
  emptyPayments: { textAlign: "center", padding: "40px 20px" },
  emptyPaymentsIcon: { fontSize: 36, marginBottom: 10 },
  emptyPaymentsTitle: { fontSize: 16, fontWeight: 700, marginBottom: 6 },
  emptyPaymentsText: { fontSize: 13, color: "#888" },
  tableWrap: { overflowX: "auto" },
  table: { width: "100%", borderCollapse: "collapse", fontSize: 14 },
  th: { textAlign: "left", padding: "10px 12px", fontSize: 12, fontWeight: 600, color: "#888", textTransform: "uppercase", borderBottom: "2px solid #F0F0F5" },
  td: { padding: "10px 12px", borderBottom: "1px solid #F8F9FC" },
  trEven: { background: "#FAFAFA" },
  typeBadge: { fontSize: 11, fontWeight: 600, padding: "2px 8px", borderRadius: 20 },
  editPaymentBtn: { background: "none", border: "none", fontSize: 14, cursor: "pointer", padding: 4, opacity: 0.6 },
  deletePaymentBtn: { background: "none", border: "none", color: "#ccc", fontSize: 14, cursor: "pointer", padding: 4 },
  modalOverlay: { position: "fixed", inset: 0, background: "rgba(0,0,0,0.45)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 1000 },
  modal: { background: "#fff", borderRadius: 16, width: "100%", maxWidth: 480, boxShadow: "0 8px 40px rgba(0,0,0,0.18)", overflow: "hidden", maxHeight: "90vh", overflowY: "auto" },
  modalHeader: { display: "flex", justifyContent: "space-between", alignItems: "center", padding: "20px 24px", borderBottom: "1px solid #F0F0F5" },
  modalTitle: { fontSize: 17, fontWeight: 700, margin: 0 },
  modalClose: { background: "none", border: "none", fontSize: 18, color: "#999", cursor: "pointer" },
  modalBody: { padding: "20px 24px", display: "flex", flexDirection: "column", gap: 14 },
  formGroup: { display: "flex", flexDirection: "column", gap: 5, flex: 1 },
  label: { fontSize: 13, fontWeight: 600, color: "#444" },
  input: { border: "1.5px solid #E5E7EB", borderRadius: 8, padding: "10px 12px", fontSize: 14, outline: "none", width: "100%", boxSizing: "border-box" },
  inputError: { borderColor: "#EF4444" },
  errorText: { fontSize: 12, color: "#EF4444" },
  splitRow: { display: "flex", gap: 12 },
  autoCalcNote: { fontSize: 12, color: "#888", background: "#F8F9FC", padding: "8px 12px", borderRadius: 8, lineHeight: 1.6 },
  modalFooter: { padding: "16px 24px", borderTop: "1px solid #F0F0F5", display: "flex", justifyContent: "flex-end", gap: 10 },
  cancelBtn: { background: "#F3F4F6", color: "#555", border: "none", borderRadius: 8, padding: "9px 18px", fontWeight: 600, fontSize: 14, cursor: "pointer" },
};
